import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LEGAL_DOCUMENT_TYPES,
  LegalDocument,
  LegalDocumentType,
  createLegalDocument,
  getLegalDocumentHistory,
  publishLegalDocument,
  updateLegalDocument,
} from "../../services/legalDocument.service";
import RichTextEditor from "../../components/common/RichTextEditor";
import Modal from "../../components/common/Modal";
import LegalDocumentPreview from "./components/LegalDocumentPreview";
import {
  CHANGE_TYPE_OPTIONS,
  VersionChangeType,
  getNextSemVer,
  normalizeVersion,
} from "../../utils/semver";
import {
  ArrowLeft,
  Loader2,
  Save,
  Rocket,
  FileText,
  AlertTriangle,
  ClipboardList,
  Eye,
  Layers,
  Sparkles,
} from "lucide-react";

type EditorTab = "edit" | "preview";

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "PUBLISHED", label: "Published" },
  { value: "ARCHIVED", label: "Archived" },
];

const extractError = (err: any): string =>
  err?.response?.data?.message || "An unexpected error occurred";

const stripHtml = (html: string): string =>
  (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

interface EditorForm {
  documentType: LegalDocumentType;
  title: string;
  content: string;
  effectiveFrom: string;
  isMandatory: boolean;
  requiresReAcceptance: boolean;
  changeType: VersionChangeType;
}

const LegalDocumentEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("documentType");
  const documentType = LEGAL_DOCUMENT_TYPES.find((t) => t.value === typeParam)?.value as
    | LegalDocumentType
    | undefined;

  const goBack = useCallback(() => navigate("/legal-content"), [navigate]);

  const [versions, setVersions] = useState<LegalDocument[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [form, setForm] = useState<EditorForm | null>(null);
  const [dedicatedType, setDedicatedType] = useState<LegalDocumentType | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishTarget, setPublishTarget] = useState<LegalDocument | null>(null);
  const [activeTab, setActiveTab] = useState<EditorTab>("edit");

  useEffect(() => {
    if (!documentType) {
      toast.error("Invalid document type");
      navigate("/legal-content", { replace: true });
      return;
    }
    setDedicatedType(documentType);
    const type = documentType;

    (async () => {
      setFetching(true);
      try {
        const hist = await getLegalDocumentHistory(type);
        setVersions(hist);

        const draft = [...hist].reverse().find((v) => v.status === "DRAFT");
        if (draft) {
          setDraftId(draft.id);
          setForm({
            documentType: draft.documentType,
            title: draft.title,
            content: draft.content || "",
            effectiveFrom: draft.effectiveFrom ? draft.effectiveFrom.slice(0, 10) : "",
            isMandatory: draft.isMandatory,
            requiresReAcceptance: draft.requiresReAcceptance,
            changeType: (draft.changeType as VersionChangeType) || "MINOR",
          });
        } else {
          setDraftId(null);
          const seed = hist.find((v) => v.status === "PUBLISHED") ?? hist[hist.length - 1] ?? null;
          const label = LEGAL_DOCUMENT_TYPES.find((t) => t.value === type)?.label || "";
          setForm({
            documentType: type,
            title: seed?.title || label,
            content: seed?.content || "",
            effectiveFrom: seed?.effectiveFrom ? seed.effectiveFrom.slice(0, 10) : "",
            isMandatory: seed ? seed.isMandatory : true,
            requiresReAcceptance: seed ? seed.requiresReAcceptance : false,
            changeType: "MINOR",
          });
        }
      } catch {
        toast.error("Failed to load legal document");
      } finally {
        setFetching(false);
      }
    })();
  }, [documentType, navigate]);

  const typeLabel = useMemo(
    () => LEGAL_DOCUMENT_TYPES.find((t) => t.value === dedicatedType)?.label || "Legal Document",
    [dedicatedType]
  );

  // Current published document and version
  const publishedDoc = useMemo(
    () => versions.find((v) => v.status === "PUBLISHED") ?? null,
    [versions]
  );

  const publishedVersion = useMemo(
    () => (publishedDoc ? normalizeVersion(publishedDoc.version) : null),
    [publishedDoc]
  );

  // Automatically calculate the proposed next version based on current published version and selected changeType
  const proposedVersion = useMemo(() => {
    if (!publishedVersion) return "1.0.0";
    return getNextSemVer(publishedVersion, form?.changeType || "MINOR");
  }, [publishedVersion, form?.changeType]);

  // Last saved timestamp of the draft being edited (null for a brand-new draft).
  const lastUpdated = useMemo(() => {
    if (!draftId) return null;
    return versions.find((v) => v.id === draftId)?.updatedAt ?? null;
  }, [draftId, versions]);

  // Document type is only selectable when no version has ever been created for it
  // (the backend treats documentType as immutable once a row exists).
  const typeLocked = versions.length > 0;

  const setField = <K extends keyof EditorForm>(key: K, value: EditorForm[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    if (errors[key as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  const validate = (forPublish: boolean): boolean => {
    if (!form) return false;
    const errs: Record<string, string> = {};

    if (typeLocked && form.documentType !== dedicatedType) {
      errs.documentType = "Document type cannot be changed once a version exists";
    }
    if (!form.title.trim()) {
      errs.title = "Document title is required";
    }
    if (forPublish && !stripHtml(form.content)) {
      errs.content = "Content is required before publishing. Add some content first.";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildPayload = () => {
    return {
      title: form!.title.trim(),
      content: form!.content,
      isMandatory: form!.isMandatory,
      requiresReAcceptance: form!.requiresReAcceptance,
      effectiveFrom: form!.effectiveFrom || null,
      changeType: form!.changeType,
      version: proposedVersion,
    };
  };

  const saveDraft = async (): Promise<LegalDocument | null> => {
    const payload = buildPayload();
    if (draftId) {
      const updated = await updateLegalDocument(draftId, payload);
      setDraftId(updated.id);
      return updated;
    }
    const created = await createLegalDocument({ documentType: form!.documentType, ...payload });
    setDraftId(created.id);
    return created;
  };

  const handleSaveDraft = async () => {
    if (!form || !validate(false)) return;
    setSaving(true);
    try {
      await saveDraft();
      toast.success("Draft saved successfully");
      goBack();
    } catch (err: any) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishRequest = async () => {
    if (!form || !validate(true)) return;
    setSaving(true);
    try {
      const saved = await saveDraft();
      setPublishTarget(saved);
    } catch (err: any) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishConfirm = async () => {
    if (!publishTarget) return;
    setPublishing(true);
    try {
      await publishLegalDocument(publishTarget.id, { changeType: form!.changeType });
      toast.success("Document version published successfully");
      setPublishTarget(null);
      goBack();
    } catch (err: any) {
      toast.error(extractError(err));
    } finally {
      setPublishing(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  const busy = saving || publishing;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            aria-label="Back to legal content"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
              {typeLabel}
              <span className="text-gray-400 font-normal"> — legal document</span>
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {draftId
                ? `Editing draft. Proposed version upon publishing is v${proposedVersion}. Historical published versions are never modified.`
                : `Creating a new draft. Proposed version upon publishing is v${proposedVersion}. The currently published version (if any) stays live until you publish.`}
            </p>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        <TabButton active={activeTab === "edit"} onClick={() => setActiveTab("edit")}>
          <ClipboardList size={16} />
          Edit
        </TabButton>
        <TabButton active={activeTab === "preview"} onClick={() => setActiveTab("preview")}>
          <Eye size={16} />
          Preview
        </TabButton>
      </div>

      {activeTab === "edit" ? (
        <>
          {/* Semantic Versioning & Change Classification */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Layers size={18} className="text-primary-600" />
                  Semantic Versioning & Change Classification
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Select the legal change type. The next SemVer number is automatically calculated.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">Current Published:</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {publishedVersion ? `v${publishedVersion}` : "None (Initial Version)"}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Version Change Type <span className="text-red-500 ml-0.5">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {CHANGE_TYPE_OPTIONS.map((opt) => {
                    const isSelected = form.changeType === opt.value;
                    const previewNext = publishedVersion
                      ? getNextSemVer(publishedVersion, opt.value)
                      : "1.0.0";

                    return (
                      <div
                        key={opt.value}
                        onClick={() => !busy && setField("changeType", opt.value)}
                        className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                          isSelected
                            ? "border-primary-600 bg-primary-50/40 shadow-sm"
                            : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                                isSelected
                                  ? "border-primary-600 bg-primary-600"
                                  : "border-gray-300 bg-white"
                              }`}
                            >
                              {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                            </span>
                            <span className="text-sm font-bold text-gray-900">{opt.label}</span>
                          </div>
                          <span
                            className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${
                              isSelected
                                ? "bg-primary-600 text-white"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            v{previewNext}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                          {opt.description}
                        </p>
                        <div className="text-[11px] text-gray-500 font-mono bg-white/80 rounded px-2 py-1 border border-gray-200 flex items-center justify-between">
                          <span className="text-gray-400">Rule:</span>
                          <span className="font-semibold text-gray-700">
                            {publishedVersion
                              ? `v${publishedVersion} → v${previewNext}`
                              : "Initial: 1.0.0"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Proposed Version Summary Callout */}
              <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-900">
                        Proposed Next Version:
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-bold bg-blue-600 text-white font-mono shadow-sm">
                        v{proposedVersion}
                      </span>
                      <span className="text-xs text-blue-700 font-medium">
                        ({form.changeType} update)
                      </span>
                    </div>
                    <p className="text-xs text-blue-800 mt-1">
                      {publishedVersion
                        ? `Incrementing from active version v${publishedVersion} based on selected ${form.changeType} change type.`
                        : `Initial version for this legal document will be published as v${proposedVersion}.`}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-500 font-mono shrink-0 sm:text-right border-t sm:border-t-0 pt-2 sm:pt-0 border-blue-100">
                  Standard: <span className="font-bold text-gray-700">MAJOR.MINOR.PATCH</span>
                </div>
              </div>
            </div>
          </div>

          {/* Document details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FileText size={18} />
                Document details
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Document Type</label>
                <select
                  value={form.documentType}
                  onChange={(e) => setField("documentType", e.target.value as LegalDocumentType)}
                  disabled={typeLocked || busy}
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors disabled:bg-gray-50 disabled:text-gray-500 ${
                    errors.documentType ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
                  }`}
                >
                  {LEGAL_DOCUMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {typeLocked && (
                  <p className="text-xs text-gray-400 mt-1">Locked after the first version is created.</p>
                )}
                {errors.documentType && <p className="text-xs text-red-600 mt-1">{errors.documentType}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Document Title <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  disabled={busy}
                  placeholder="Document title"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors disabled:bg-gray-50 ${
                    errors.title ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"
                  }`}
                />
                {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Proposed Version</label>
                <input
                  type="text"
                  value={`v${proposedVersion}`}
                  readOnly
                  disabled
                  className="w-full px-3 py-2.5 border border-primary-200 bg-primary-50/30 text-primary-900 rounded-lg text-sm font-bold font-mono"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Format: MAJOR.MINOR.PATCH (calculated from {form.changeType} update)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Effective Date</label>
                <input
                  type="date"
                  value={form.effectiveFrom}
                  onChange={(e) => setField("effectiveFrom", e.target.value)}
                  disabled={busy}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-gray-700 disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <select
                  value="DRAFT"
                  disabled
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Draft while editing. Publishing will activate v{proposedVersion} as the published version.
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FileText size={18} />
                Content
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Supports headings, paragraphs, numbered sections, bullet lists, bold text and links.
              </p>
            </div>
            <div className="p-6">
              <RichTextEditor
                value={form.content}
                onChange={(html) => setField("content", html)}
                placeholder="Start typing the legal content... Use the toolbar to format headings, lists, bold text and links."
                minHeight={360}
                error={!!errors.content}
              />
              {errors.content && <p className="text-xs text-red-600 mt-1.5">{errors.content}</p>}
            </div>
          </div>

          {/* Acceptance settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle size={18} />
                Acceptance settings
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Controls how users are required to accept or re-accept this document. Re-acceptance is explicitly controlled by Super Admin and is not automatically forced by version change type.
              </p>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mandatory Document</label>
                <YesNoRadio
                  name="isMandatory"
                  value={form.isMandatory}
                  onChange={(val) => setField("isMandatory", val)}
                  disabled={busy}
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  If yes, users cannot proceed without accepting this document.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requires Re-acceptance
                </label>
                <YesNoRadio
                  name="requiresReAcceptance"
                  value={form.requiresReAcceptance}
                  onChange={(val) => setField("requiresReAcceptance", val)}
                  disabled={busy}
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  If yes, users must accept again when published. If no, previous user acceptances remain valid.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <LegalDocumentPreview
          title={form.title}
          version={proposedVersion}
          effectiveFrom={form.effectiveFrom}
          updatedAt={lastUpdated}
          content={form.content}
        />
      )}

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={goBack}
          disabled={busy}
          className="px-4 py-2.5 rounded-lg border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors disabled:opacity-50 text-sm font-medium"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-primary-300 text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50 text-sm font-medium"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save Draft (v{proposedVersion})
        </button>
        <button
          type="button"
          onClick={handlePublishRequest}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
          Publish v{proposedVersion}
        </button>
      </div>

      {/* Publish Review & Confirmation Modal */}
      {publishTarget && (
        <Modal
          isOpen={publishTarget !== null}
          title="Review & Publish Legal Document"
          subtitle="Review the final version numbers and acceptance configuration before publishing"
          onClose={() => !publishing && setPublishTarget(null)}
          className="max-w-lg"
        >
          <div className="p-6 space-y-5">
            <div className="bg-gray-50 border border-gray-200 rounded-lg divide-y divide-gray-200">
              <div className="p-3.5 flex items-center justify-between text-sm">
                <span className="text-gray-500 font-medium">Document</span>
                <span className="font-semibold text-gray-900">{form.title}</span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-sm">
                <span className="text-gray-500 font-medium">Current Published Version</span>
                <span className="font-mono text-gray-700 font-medium">
                  {publishedVersion ? `v${publishedVersion}` : "None (Initial Version)"}
                </span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-sm">
                <span className="text-gray-500 font-medium">Selected Change Classification</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase bg-blue-100 text-blue-800 border border-blue-200">
                  {form.changeType}
                </span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-sm bg-emerald-50/60">
                <span className="text-emerald-900 font-semibold">New Version to Publish</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded text-sm font-bold bg-emerald-600 text-white font-mono shadow-sm">
                  v{proposedVersion}
                </span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-sm">
                <span className="text-gray-500 font-medium">Re-acceptance Required</span>
                <span className={`font-semibold ${form.requiresReAcceptance ? "text-emerald-700" : "text-gray-600"}`}>
                  {form.requiresReAcceptance ? "Yes (Users must accept again)" : "No (Previous acceptances remain valid)"}
                </span>
              </div>
              <div className="p-3.5 flex items-center justify-between text-sm">
                <span className="text-gray-500 font-medium">Mandatory Document</span>
                <span className="font-semibold text-gray-900">
                  {form.isMandatory ? "Yes (Blocking until accepted)" : "No (Optional)"}
                </span>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed">
              Publishing will activate version <strong>v{proposedVersion}</strong> immediately across the platform. The previously published version (if any) will be archived into historical records.
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPublishTarget(null)}
                disabled={publishing}
                className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePublishConfirm}
                disabled={publishing}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors disabled:opacity-50"
              >
                {publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                Publish Version {proposedVersion}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* --- Helpers --- */

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
      active
        ? "border-primary-600 text-primary-700"
        : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
    }`}
  >
    {children}
  </button>
);

const YesNoRadio = ({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string;
  value: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) => (
  <div className="flex items-center gap-4">
    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={value === true}
        onChange={() => onChange(true)}
        disabled={disabled}
        className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
      />
      Yes
    </label>
    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={value === false}
        onChange={() => onChange(false)}
        disabled={disabled}
        className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
      />
      No
    </label>
  </div>
);

export default LegalDocumentEditor;